import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, tap} from 'rxjs';
import {
  ClientEntity,
  DossierItem,
  DossierItemEntity,
  IntermediaryEntity,
  RelationSearchResult,
  RelationSearchResults, ServiceBerichtEntity
} from '../../index';
import {SelectFormControlItem} from '@snelstart/ui-form-controls';

@Injectable({providedIn: 'root'})
export class BokkzAlgDataService {
  private initialYear: number = Number(localStorage.getItem('year')) || new Date().getFullYear();
  public client = new BehaviorSubject<ClientEntity>(this.getFromSessionStorage<ClientEntity>('client') || ({} as ClientEntity));
  public intermediary = new BehaviorSubject<IntermediaryEntity>(this.getFromSessionStorage<IntermediaryEntity>('intermediary') || ({} as IntermediaryEntity));
  public options = new BehaviorSubject<SelectFormControlItem<string>[]>(
    this.getFromSessionStorage<SelectFormControlItem<string>[]>('options') ||
    ([
      {
        value: '0',
        label: 'Geen SnelStart administratie'
      }
    ] as SelectFormControlItem<string>[])
  );
  public clientList = new BehaviorSubject<ClientEntity[]>(this.getFromSessionStorage<ClientEntity[]>('clientList') || ({} as ClientEntity[]));
  public clientListMigration = new BehaviorSubject<ClientEntity[]>(this.getFromSessionStorage<ClientEntity[]>('clientListMigration') || ({} as ClientEntity[]));
  public token = new BehaviorSubject<string | null>(null);
  private relations$ = new BehaviorSubject<RelationSearchResults | null>(null);
  public dossierList = new BehaviorSubject<DossierItem[]>(this.getFromSessionStorage<DossierItem[]>('dossierList') || ({} as DossierItem[]));
  public isIncomeTax = new BehaviorSubject<boolean>(false);
  public isCorporateTax = new BehaviorSubject<boolean>(false);
  public pdfIbRapport = new BehaviorSubject<string>(this.getFromSessionStorage<string>('pdfIbRapport') || ({} as string));
  public pdfVpbRapport = new BehaviorSubject<string>(this.getFromSessionStorage<string>('pdfVpbRapport') || ({} as string));
  public relation = new BehaviorSubject<RelationSearchResult>(this.getFromSessionStorage<RelationSearchResult>('relation') || ({} as RelationSearchResult));
  public selectedYear = new BehaviorSubject<number>(this.initialYear);
  public selectedService = new BehaviorSubject<string>('null');
  public workflowItem = new BehaviorSubject<DossierItemEntity>(this.getFromSessionStorage<DossierItemEntity>('dossierItemEntity') || <DossierItemEntity>{});
  public sstRelationId = new BehaviorSubject<string>('');
  public serviceBerichtXbrl = new BehaviorSubject<string>('');
  private serviceBericht = new BehaviorSubject<ServiceBerichtEntity | null>(null);

  public serviceBericht$ = this.serviceBericht.asObservable();
  public clientEntity$ = this.client.asObservable();
  public intermediaryEntity$ = this.intermediary.asObservable();
  public selectedYear$ = this.selectedYear.asObservable();
  public workflowItem$ = this.workflowItem.asObservable();

  constructor() {
    sessionStorage.clear();
    this.applyDebounceTime();
  }

  private applyDebounceTime() {
    const debounceDelay = 500; // Timeout voor toetsemaanslagen
    this.client = this.client.pipe(tap((value) => this.saveToSessionStorage('client', value))) as BehaviorSubject<ClientEntity>;
    this.intermediary = this.intermediary.pipe(
      tap((value) => this.saveToSessionStorage('intermediary', value))
    ) as BehaviorSubject<IntermediaryEntity>;
    this.options = this.options.pipe(tap((value) => this.saveToSessionStorage('options', value))) as BehaviorSubject<
      SelectFormControlItem<string>[]
    >;
    this.clientList = this.clientList.pipe(tap((value) => this.saveToSessionStorage('clientList', value))) as BehaviorSubject<
      ClientEntity[]
    >;
    this.dossierList = this.dossierList.pipe(tap((value) => this.saveToSessionStorage('dossierList', value))) as BehaviorSubject<
      DossierItem[]
    >;
    this.pdfIbRapport = this.pdfIbRapport.pipe(
      tap((value) => this.saveToSessionStorage('pdfIbRapport', value))
    ) as BehaviorSubject<string>;
    this.pdfVpbRapport = this.pdfVpbRapport.pipe(
      tap((value) => this.saveToSessionStorage('pdfVpbRapport', value))
    ) as BehaviorSubject<string>;
  }

  //Voor het ophalen uit de sessionstorage.
  private getFromSessionStorage<T>(key: string): T | null {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }

  //Voor het opslaan in sessionstorage
  private saveToSessionStorage(key: string, value: any) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  private removeNullAndEmptyArrays(obj: any): any {
    if (Array.isArray(obj)) {
      return obj
        .filter((item) => item !== null && !(Array.isArray(item) && item.length === 0))
        .map((item) => this.removeNullAndEmptyArrays(item));
    } else if (typeof obj === 'object' && obj !== null) {
      const newObj: any = {};
      Object.entries(obj).forEach(([key, value]) => {
        const cleanedValue = this.removeNullAndEmptyArrays(value);
        if (cleanedValue !== null && !(Array.isArray(cleanedValue) && cleanedValue.length === 0)) {
          newObj[key] = cleanedValue;
        }
      });
      return Object.keys(newObj).length === 0 ? null : newObj;
    } else {
      return obj;
    }
  }

  updateRelations(data: RelationSearchResults): void {
    this.relations$.next(data);
  }

  getRelations(): Observable<RelationSearchResults | null> {
    return this.relations$.asObservable();
  }

  get currentRelations(): RelationSearchResults | null {
    return this.relations$.value;
  }

  updateClient(client: ClientEntity) {
    const cleanedClient = this.removeNullAndEmptyArrays(client);
    this.client.next(cleanedClient);
  }

  updateClientList(clientList: ClientEntity[]) {
    this.clientList.next(clientList);
  }

  updateClientListMigration(clientListMigration: ClientEntity[]) {
    this.clientListMigration.next(clientListMigration);
  }

  updateToken(token: string) {
    this.token.next(token);
  }

  updateIntermediary(intermediary: IntermediaryEntity) {
    this.intermediary.next(intermediary);
  }

  updateSelectedYear(value: number) {
    this.selectedYear.next(value);
    localStorage.setItem('year', value.toString());
  }

  updateSelectedService(value: string) {
    this.selectedService.next(value);
  }

  updateWorkflowItem(value: DossierItemEntity) {
    this.workflowItem.next(value);
  }

  updateSstRelationId(value: string) {
    this.sstRelationId.next(value);
  }

  updateServiceBerichtXbrl(value: string) {
    this.serviceBerichtXbrl.next(value);
  }

  updateTaxTypeList(dossierList: DossierItem[]) {
    this.dossierList.next(dossierList);
    if (dossierList.some((item) => item.soort === 'VPB')) {
      this.isCorporateTax.next(true);
    }
    if (dossierList.some((item) => item.soort === 'IB')) {
      this.isIncomeTax.next(true);
    }
  }

  updateIbPdfRapport(result: string) {
    this.pdfIbRapport.next(result);
  }

  updateClientListWithClient(result: ClientEntity) {
    const clientList = this.getFromSessionStorage('clientList') as ClientEntity[];
    if (clientList) {
      let clientFound = {} as number;
      if (result.deleted) {
        clientFound = clientList.findIndex((client) => client.id === result.id);
        if (clientFound !== -1) {
          clientList.splice(clientFound, 1);
        }
      } else {
        if (clientList.length > 0) {
          clientFound = clientList.findIndex((client) => client.id === result.id);
          if (clientFound !== -1) {
            clientList[clientFound] = result;
          } else {
            // Add the new client
            clientList.push(result);
          }
        }
      }
      this.clientList.next(clientList);
    }
  }

  updateServiceBericht(entity: ServiceBerichtEntity): void {
    this.serviceBericht.next(entity);
  }

  private gecontroleerdeItemsInSessie = new Set<number>();

  public isGecontroleerd(id: number): boolean {
    return this.gecontroleerdeItemsInSessie.has(id);
  }

  public markeerAlsGecontroleerd(id: number): void {
    this.gecontroleerdeItemsInSessie.add(id);
  }

  public markeerAlsNietGecontroleerd(id: number): void {
    this.gecontroleerdeItemsInSessie.delete(id);
  }

  getServiceFromUrl(params: string) {
    if (params.includes('tax-income')) {
      this.updateSelectedService('IB');
      return 'IB';
    }
    if (params.includes('tax-corporate')) {
      this.updateSelectedService('VPB');
      return 'VPB';
    }
    if (params.includes('tax-allowance')) {
      this.updateSelectedService('TSL');
      return 'TSL';
    }
    if (params.includes('pm-report')) {
      this.updateSelectedService('JR');
      return 'JR';
    }
    if (params.includes('tax-dividend')) {
      this.updateSelectedService('DB');
      return 'DB';
    }
    if (params.toString().includes('tax-donation')) {
      this.updateSelectedService('SB');
      return 'SB';
    }
    if (params.toString().includes('tax-vat')) {
      this.updateSelectedService('BTW');
      return 'BTW';
    }
    if (params.toString().includes('tax-wage')) {
      this.updateSelectedService('WAGE');
      return 'WAGE';
    } else return 'UNKNOWN';
  }
}
